<!--
  MembraneIntegration.vue

  Thin router: resolves the integration from the `:appKey` route param via the
  app-wide integrations store, resolves its optional task-sync config, and
  delegates all rendering to IntegrationSyncWorkflow. Integrations that have a
  config (Gmail, GitHub, Todoist, …) get the full task-sync wizard; integrations
  without one get the same account + data-sources layout
  with only "Add data source" as the CTA.
-->
<template>
  <IntegrationSyncWorkflow
    v-if="app"
    :org-id="orgId"
    :app="app"
    :config="integrationSyncConfig"
  />
</template>

<script lang="ts">
import { computed, defineComponent, watch } from "vue";

import * as inject from "@/components/inject";
import { integrationSyncConfigs } from "@/components/settings/integrations/integrationSyncConfigRegistry";
import IntegrationSyncWorkflow from "@/components/settings/integrations/IntegrationSyncWorkflow.vue";
import type { IntegrationSyncConfig } from "@/components/settings/integrations/useIntegrationSyncWorkflow";
import { RouteNames } from "@/routes/types";

export default defineComponent({
  components: { IntegrationSyncWorkflow },
  props: {
    orgId: { type: String, required: true },
    appKey: { type: String, required: true },
  },
  setup(props) {
    const router = inject.router();
    const integrations = inject.integrations();

    const view = integrations.forOrganization(computed(() => props.orgId));
    const app = computed(() => view.getServiceByKey(props.appKey));

    // Unknown key once the organization's services have loaded (bad deep
    // link, integration removed from Membrane, failed load): replace-redirect
    // to the integrations list, which can render its own error state.
    watch(
      [app, view.loading],
      ([resolved, loading]) => {
        if (loading || resolved) return;
        void router.replace({
          name: RouteNames.SettingsIntegrations,
          params: { orgId: props.orgId },
        });
      },
      { immediate: true }
    );

    const integrationSyncConfig = computed<IntegrationSyncConfig | null>(
      () => integrationSyncConfigs[props.appKey] ?? null
    );
    return { app, integrationSyncConfig };
  },
});
</script>
